<template>
  <div class="orange-egg" :class="{ 'orange-egg--fading': fading }" aria-hidden="true">
    <div class="orange-egg__grid">
      <svg v-for="(hex, i) in hexes" :key="i" class="orange-egg__hex"
           viewBox="-50 -50 100 100" :style="hex">
        <polygon points="-20,36 -40,0 -20,-36 20,-36 40,0 20,36"/>
      </svg>
    </div>
  </div>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref} from "vue";
import {FIXED_GRID_SIZE} from "@/composables/challenges";

const emit = defineEmits(['done']);

const PLAY_MS = 9200;
const FADE_MS = 1500;

const fading = ref(false);
let fadeTimer = null;
let doneTimer = null;

// Every cell contributes one hexagon. Cells are ranked by distance from the
// grid centre and grouped four-per-ring: when the swarm converges, each group
// stacks onto one of nine nested rings twisted ~22° apart — a vortex after
// Rosalie de Meric's "Hexagon Sequence II", rendered in shades of orange
// (burnt rim to glowing core) so the twist stays visible in one colour
// family. For the finale every ring spins forward to 360° while its shade
// settles on the game orange, resolving into one big flat orange hexagon.
const mid = (FIXED_GRID_SIZE - 1) / 2;
const cells = [];
for (let r = 0; r < FIXED_GRID_SIZE; r++) {
  for (let c = 0; c < FIXED_GRID_SIZE; c++) {
    cells.push({r, c, dist: Math.hypot(r - mid, c - mid)});
  }
}
cells.sort((a, b) => a.dist - b.dist);

// Ring fills, innermost first — all orange, light core out to burnt rim,
// centred on --geogaddi-o.
const RING_COLORS = [
  "#ffd0a0",
  "#fcb97e",
  "#faa35e",
  "#f78d43",
  "#F3782D", // --geogaddi-o
  "#d9631f",
  "#bf4e16",
  "#9c3a12",
  "#7a260d",
];

const RING_COUNT = RING_COLORS.length;
const perRing = cells.length / RING_COUNT;
const hexes = [];
cells.forEach((cell, rank) => {
  const ring = Math.floor(rank / perRing);
  const t = ring / (RING_COUNT - 1); // 0 = innermost ring, 1 = outermost
  const leftPct = ((cell.c + 0.5) / FIXED_GRID_SIZE) * 100;
  const topPct = ((cell.r + 0.5) / FIXED_GRID_SIZE) * 100;
  hexes.push({
    left: `${leftPct}%`,
    top: `${topPct}%`,
    zIndex: RING_COUNT - ring,
    '--tx': `${(50 - leftPct).toFixed(2)}cqw`,
    '--ty': `${(50 - topPct).toFixed(2)}cqh`,
    // All four hexagons of a ring land at exactly the same angle and scale
    // so the stacked copies read as one crisp shape.
    // The delay is a pure function of the ring so all four copies of a ring
    // move in perfect sync — any per-copy offset shows as ragged edges
    // during the closing spin.
    '--rs': (0.65 + t * 5.1).toFixed(2),
    '--rr': `${ring * 22}deg`,
    '--ringColor': RING_COLORS[ring],
    '--delay': `${Math.round((1 - t) * 1500)}ms`,
  });
});

onMounted(() => {
  fadeTimer = setTimeout(() => { fading.value = true; }, PLAY_MS);
  doneTimer = setTimeout(() => emit('done'), PLAY_MS + FADE_MS);
});

onBeforeUnmount(() => {
  clearTimeout(fadeTimer);
  clearTimeout(doneTimer);
});
</script>

<style scoped>
.orange-egg {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(14, 14, 14, 0.92);
  pointer-events: none;
  animation: egg-in 0.8s ease both;
}

.orange-egg--fading {
  opacity: 0;
  transition: opacity 1.5s ease;
}

.orange-egg__grid {
  position: absolute;
  inset: 0;
  max-width: 720px;
  max-height: 720px;
  margin: auto;
  aspect-ratio: 1;
  container-type: size;
  animation: egg-spin 10.7s ease-in-out both;
}

.orange-egg__hex {
  /* Rendered at 2× the cell-phase display size so the big outer rings only
     need ~6× upscaling and keep crisp edges. */
  position: absolute;
  width: 104px;
  height: 104px;
  margin: -52px 0 0 -52px;
  fill: var(--geogaddi-o);
  animation: egg-flow 7.5s var(--delay, 0ms) cubic-bezier(0.45, 0, 0.2, 1) both;
}

@keyframes egg-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* The whole vortex turns slowly once the rings have nested */
@keyframes egg-spin {
  0% { transform: rotate(0deg); }
  45% { transform: rotate(0deg); }
  100% { transform: rotate(30deg); }
}

/* Per hex: breathe in place, glide to the centre onto its twisted ring
   (taking on its ring's shade of orange), hold the vortex, then spin forward
   to 360° while settling back on the game orange — one big flat hexagon */
@keyframes egg-flow {
  0% {
    transform: translate(0, 0) scale(0.15) rotate(0deg);
    opacity: 0;
    fill: var(--geogaddi-o);
  }
  12% {
    opacity: 1;
  }
  30% {
    transform: translate(0, 0) scale(0.28) rotate(16deg);
    fill: var(--geogaddi-o);
  }
  58% {
    transform: translate(var(--tx), var(--ty)) scale(var(--rs)) rotate(var(--rr));
    fill: var(--ringColor);
  }
  72% {
    transform: translate(var(--tx), var(--ty)) scale(var(--rs)) rotate(var(--rr));
    fill: var(--ringColor);
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(var(--rs)) rotate(360deg);
    opacity: 1;
    fill: var(--geogaddi-o);
  }
}

@media (prefers-reduced-motion: reduce) {
  .orange-egg__grid,
  .orange-egg__hex {
    animation: none;
  }
}
</style>
